import React, { useCallback, useEffect, useState } from "react";
import Modal from "../../components/Modal";
import { Spinner } from "../../components/Loader";
import { errorMessage } from "../../services";
import { getSites } from "../../services/sites";
import { createCoupon, deleteCoupon, getCoupons, updateCoupon } from "../../services/coupons";
import { money } from "../../utils/format";

const EMPTY = { code: "", description: "", type: "percent", value: "", minAmount: "", maxDiscount: "", usageLimit: "", onePerCustomer: true, plans: [], expiresAt: "", active: true };
const formOf = (c) => ({
  ...EMPTY,
  ...c,
  value: c.value ?? "",
  minAmount: c.minAmount || "",
  maxDiscount: c.maxDiscount || "",
  usageLimit: c.usageLimit || "",
  expiresAt: c.expiresAt ? c.expiresAt.slice(0, 10) : "",
});
const offText = (c) => (c.type === "percent" ? `${c.value}% off${c.maxDiscount ? ` (up to ${money(c.maxDiscount)})` : ""}` : `${money(c.value)} off`);
const dateText = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
// The end date chosen covers that whole day (India time)
const endOfDay = (yyyyMmDd) => (yyyyMmDd ? new Date(`${yyyyMmDd}T23:59:59+05:30`).toISOString() : null);

// Marketing -> Discount coupons. A Super Admin makes Ownezzy's coupons for buying website plans; a store owner picks
// a website and makes its coupons for shoppers' orders.
export default function CouponsManager({ user, notify }) {
  const ownezzy = user.role === "superadmin";
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [coupons, setCoupons] = useState(null); // null while loading
  const [editing, setEditing] = useState(null); // the form, with _id when editing one
  const [busy, setBusy] = useState(false);
  const scope = ownezzy ? null : siteId;

  useEffect(() => {
    if (ownezzy) return;
    getSites({ limit: 100, sort: "oldest" }).then(({ data }) => {
      setSites(data.sites);
      if (data.sites[0]) setSiteId(data.sites[0].id);
      else setCoupons([]);
    }).catch((err) => notify(errorMessage(err), "error"));
  }, [ownezzy, notify]);

  const load = useCallback(() => {
    if (!ownezzy && !siteId) return;
    setCoupons(null);
    getCoupons(scope).then(({ data }) => setCoupons(data.coupons)).catch((err) => { setCoupons([]); notify(errorMessage(err), "error"); });
  }, [ownezzy, siteId, scope, notify]);
  useEffect(() => { load(); }, [load]);

  const set = (key) => (e) => setEditing((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  // No plans saved = both; at least one stays ticked
  const togglePlan = (plan) => setEditing((f) => {
    const current = f.plans.length ? f.plans : ["monthly", "yearly"];
    const next = current.includes(plan) ? current.filter((p) => p !== plan) : [...current, plan];
    if (!next.length) return f;
    return { ...f, plans: next.length === 2 ? [] : next };
  });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      code: editing.code.trim(),
      description: editing.description.trim(),
      type: editing.type,
      value: Number(editing.value),
      minAmount: Number(editing.minAmount) || 0,
      maxDiscount: editing.type === "percent" ? Number(editing.maxDiscount) || 0 : 0,
      usageLimit: Number(editing.usageLimit) || 0,
      onePerCustomer: editing.onePerCustomer,
      expiresAt: endOfDay(editing.expiresAt),
      active: editing.active,
      ...(ownezzy ? { plans: editing.plans } : {}),
    };
    try {
      const { data } = editing._id ? await updateCoupon(scope, editing._id, payload) : await createCoupon(scope, payload);
      notify(data.message);
      setEditing(null);
      load();
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      const { data } = await updateCoupon(scope, c._id, { active: !c.active });
      notify(data.message);
      load();
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };
  const remove = async (c) => {
    try {
      const { data } = await deleteCoupon(scope, c._id);
      notify(data.message);
      load();
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };

  const expired = (c) => c.expiresAt && new Date(c.expiresAt) < new Date();
  return (
    <section className="panel coupons-panel tab-gap">
      <div className="panel-heading">
        <div>
          <h2>Discount coupons</h2>
          <p>{ownezzy ? "Codes sellers enter when they buy a website plan (sign-up and Pay now)." : "Codes your customers enter at checkout to get a discount."}</p>
        </div>
        <button className="primary-btn" disabled={!ownezzy && !siteId} onClick={() => setEditing({ ...EMPTY })}>＋ New coupon</button>
      </div>
      {!ownezzy && sites.length > 1 && (
        <label className="coupons-site">Website
          <select value={siteId} onChange={(e) => setSiteId(e.target.value)}>{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
        </label>
      )}
      {coupons === null ? (
        <div className="coupons-empty"><Spinner /> Loading coupons</div>
      ) : !coupons.length ? (
        <p className="coupons-empty">{!ownezzy && !sites.length ? "You have no websites yet." : "No coupons yet. Create one and share the code with your customers."}</p>
      ) : (
        <ul className="coupons-list">
          {coupons.map((c) => (
            <li key={c._id} className={c.active && !expired(c) ? "" : "is-off"}>
              <div className="coupon-code">{c.code}</div>
              <div className="coupon-info">
                <b>{offText(c)}{c.minAmount ? ` · on ${money(c.minAmount)}+` : ""}</b>
                <small>
                  {c.used} used{c.usageLimit ? ` of ${c.usageLimit}` : ""}
                  {c.onePerCustomer ? " · once per customer" : ""}
                  {ownezzy && c.plans.length ? ` · ${c.plans.join(" & ")} only` : ""}
                  {c.expiresAt ? ` · ${expired(c) ? "ended" : "ends"} ${dateText(c.expiresAt)}` : ""}
                  {!c.active ? " · paused" : ""}
                </small>
                {c.description && <small>{c.description}</small>}
              </div>
              <div className="row-actions">
                <button className="edit-action" onClick={() => setEditing(formOf(c))}>Edit</button>
                <button className="toggle-action" onClick={() => toggleActive(c)}>{c.active ? "Pause" : "Turn on"}</button>
                <button className="delete-action" onClick={() => remove(c)}>Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <Modal eyebrow="DISCOUNT COUPON" title={editing._id ? `Edit ${editing.code}` : "New coupon"} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="coupon-form">
            <div className="form-row">
              <label className="admin-field">Code<input value={editing.code} onChange={set("code")} placeholder="e.g. DIWALI20" maxLength={30} required style={{ textTransform: "uppercase" }} /></label>
              <label className="admin-field">Discount type
                <select value={editing.type} onChange={set("type")}><option value="percent">Percent (%)</option><option value="flat">Fixed amount (₹)</option></select>
              </label>
            </div>
            <div className="form-row">
              <label className="admin-field">{editing.type === "percent" ? "Percent off" : "Rupees off"}<input type="number" min="1" max={editing.type === "percent" ? 100 : undefined} value={editing.value} onChange={set("value")} required /></label>
              {editing.type === "percent" && <label className="admin-field">Most it takes off (₹, optional)<input type="number" min="0" value={editing.maxDiscount} onChange={set("maxDiscount")} placeholder="No limit" /></label>}
            </div>
            <div className="form-row">
              <label className="admin-field">{ownezzy ? "Plan price at least (₹)" : "Order at least (₹)"}<input type="number" min="0" value={editing.minAmount} onChange={set("minAmount")} placeholder="Any amount" /></label>
              <label className="admin-field">Total uses (optional)<input type="number" min="0" value={editing.usageLimit} onChange={set("usageLimit")} placeholder="No limit" /></label>
            </div>
            <label className="admin-field">Last day (optional)<input type="date" value={editing.expiresAt} onChange={set("expiresAt")} /></label>
            <label className="admin-field">Note shown with the coupon (optional)<input value={editing.description} onChange={set("description")} maxLength={150} placeholder="e.g. Diwali sale" /></label>
            {ownezzy && (
              <div className="check-row coupon-plans">
                <span>Works on:</span>
                <label><input type="checkbox" checked={!editing.plans.length || editing.plans.includes("monthly")} onChange={() => togglePlan("monthly")} />Monthly</label>
                <label><input type="checkbox" checked={!editing.plans.length || editing.plans.includes("yearly")} onChange={() => togglePlan("yearly")} />Yearly</label>
              </div>
            )}
            <div className="check-row"><label><input type="checkbox" checked={editing.onePerCustomer} onChange={set("onePerCustomer")} />{ownezzy ? "Once per WhatsApp number and email" : "Once per customer"}</label></div>
            <div className="check-row"><label><input type="checkbox" checked={editing.active} onChange={set("active")} />Active</label></div>
            <div className="product-modal-actions">
              <button type="button" className="outline-btn" onClick={() => setEditing(null)}>Cancel</button>
              <button className="primary-btn" disabled={busy}>{busy ? <><Spinner /> Saving</> : "Save coupon"}</button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}
